import { useUrlChange } from '@insify/objectiv';
import { makeContentContext } from '@objectiv/schema';
import { useVisibleEventTracker } from '@objectiv/tracker-react';
import { useTranslation } from 'react-i18next';
import { Navigate, RouterProvider, redirect } from 'react-router-dom';

import { usePolicies } from '../api/queries/usePolicies';
import { ErrorBoundaryFallback } from '../components/ErrorBoundaryFallback';
import { Impersonate } from '../components/Impersonate';
import { ChatInterface } from '../components/chat/interface';
import { sentryCreateBrowserRouter } from '../utils/sentry';

import { AuthRoute } from './AuthRoute';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicRoute } from './PublicRoute';
import { ChatLayout } from './layouts/ChatLayout/ChatLayout';
import { LayoutWithSidebar } from './layouts/LayoutWithSidebar';
import { EmailSentPage, InvalidLoginPage, LoginPage } from './pages';
import { AssistantPage } from './pages/AssistantPage/AssistantPage';
import { ErrorPage } from './pages/ErrorPage/ErrorPage';
import { ForgotEmailPage } from './pages/ForgotEmailPage/ForgotEmailPage';
import { HomePage } from './pages/HomePage/HomePage';
import { InvoicesPage } from './pages/InvoicesPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { ProductPage } from './pages/ProductsPage';
import { SlideDesignerPage } from './pages/SlideDesignerPage';

import type { Bundle } from '../types/Policy';
import type { LoaderFunction } from 'react-router-dom';

const PAGEVIEW_CONTEXT_ID = 'pageview';
const PAGE_LOADED_CONTEXT_ID = 'pageloaded';

const AppRouter = () => {
  const { t } = useTranslation('policy');
  const { data: policies } = usePolicies();

  const trackPageview = useVisibleEventTracker({
    locationStack: [makeContentContext({ id: PAGEVIEW_CONTEXT_ID })],
  });

  const trackPageLoaded = useVisibleEventTracker({
    locationStack: [makeContentContext({ id: PAGE_LOADED_CONTEXT_ID })],
  });

  useUrlChange(trackPageview, trackPageLoaded, true);

  // TODO: export routes to separate folders > file structures and lazy load everything
  const policyIdLoaderFn: LoaderFunction = async ({ params }) => {
    if (policies === undefined) {
      return null;
    }
    const bundle = policies.find((p) => p.id === params.id);

    if (!bundle) {
      return redirect('/products');
    }
    return bundle;
  };

  const router = sentryCreateBrowserRouter([
    {
      errorElement: <ErrorBoundaryFallback />,
      children: [
        {
          element: <AuthRoute />,
          children: [
            { path: '/login', element: <LoginPage /> },
            { path: '/email-sent', element: <EmailSentPage /> },
            { path: '/invalid-login', element: <InvalidLoginPage /> },
            { path: '/forgot-email', element: <ForgotEmailPage /> },
          ],
        },
        {
          element: <ProtectedRoute />,
          children: [
            {
              element: <LayoutWithSidebar />,
              children: [
                {
                  path: '/',
                  element: <HomePage />,
                  handle: {
                    crumb: () => 'home',
                  },
                },
                {
                  path: '/invoices',
                  element: <InvoicesPage />,
                  handle: {
                    crumb: () => 'invoices',
                  },
                },
                {
                  path: '/products',
                  handle: {
                    crumb: () => 'products',
                  },
                  children: [
                    {
                      index: true,
                      element: <ProductPage />,
                    },
                    {
                      path: '/products/:id',
                      element: <ProductDetailPage />,
                      loader: policyIdLoaderFn,
                      handle: {
                        crumb: (bundle: Bundle) => {
                          if (!bundle) {
                            return null;
                          }
                          const policyId = (
                            bundle.coverages.length > 0 ? bundle.coverages[0].policyNumber : null
                          ) as string;
                          const type =
                            bundle.type === 'company'
                              ? bundle.companyName
                              : bundle.type === 'personal'
                              ? t('policy_type.disability')
                              : null;
                          return {
                            crumbTitle: policyId,
                            header: type,
                          };
                        },
                      },
                    },
                  ],
                },
              ],
            },
            {
              element: <ChatLayout />,
              children: [
                {
                  path: '/my-safety-net',
                  element: <AssistantPage />,
                  handle: {
                    crumb: () => 'insify_assistant',
                  },
                },
              ],
            },
          ],
        },
        {
          element: <ProtectedRoute checkIsAdmin />,
          children: [
            { path: 'slide-designer', element: <SlideDesignerPage /> },
            {
              element: <LayoutWithSidebar />,
              children: [{ path: '/impersonate', element: <Impersonate /> }],
            },
          ],
        },
        {
          element: <PublicRoute />,
          children: [{ path: '/assistant', element: <ChatInterface /> }],
        },
        { path: '/error', element: <ErrorPage /> },
        { path: '*', element: <Navigate to='/' replace /> },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
};

export { AppRouter };
